import { supabase } from './SupabaseClient.js';
import { REPRISE_TUNING } from '../../data/repriseTuning.js';

// Reprise (REPRISE.md) talks straight to Supabase — the world tables and the
// contribute_resonance RPC live there (supabase/reprise-schema.sql), unlike
// the leaderboard which goes through the self-hosted survivor-api. Same
// offline-tolerant contract as LeaderboardClient/CloudSync, though: every
// call is wrapped so an unconfigured or unreachable Supabase degrades to
// "no world" / "contribution skipped", never a thrown error into the game.
// Reprise is additive on top of a fully playable offline game.

/**
 * Turns a finished run into a Resonance amount (REPRISE.md §3: bosses,
 * survival time, and Ascension all feed it). Kept deliberately small and
 * legible — the server re-clamps to its own per-run ceiling
 * (contribute_resonance's v_run_cap) regardless of what this returns, so
 * this only has to be roughly fair, not trusted.
 */
export function calcResonance({ bossesDefeated = 0, survivalSeconds = 0, ascensionLevel = 0, actId = 'act1' } = {}) {
  // Each act contributes at its own run-worth (data/repriseTuning.js) — they
  // are different lengths and difficulties, so one flat rate would misprice
  // one of them. Anything unrecognised (the Tide, Boss Challenge) falls back
  // to the baseline, which is what the server's per-run cap was sized against.
  const perBoss = REPRISE_TUNING[`${actId}ResonancePerBoss`] ?? REPRISE_TUNING.resonancePerBoss;
  const fromBosses = Math.max(0, bossesDefeated) * perBoss;
  const fromTime = Math.floor(Math.max(0, survivalSeconds)) * REPRISE_TUNING.resonancePerSecond;
  // Ascension scales the whole run's worth rather than adding a flat block,
  // so a hard clear is meaningfully more valuable to the world than an easy
  // one of the same length.
  const ascensionScale = 1 + REPRISE_TUNING.resonanceAscensionStep * Math.max(0, ascensionLevel);
  return Math.round((fromBosses + fromTime) * ascensionScale);
}

export const RepriseClient = {
  /**
   * The active season plus every district cell, for the map UI. Districts
   * are publicly readable, so this resolves for logged-out players too —
   * they can see the world even though they can't change it. Returns
   * `{ ok, season, districts }`; `ok:false` means no Supabase / no active
   * season / a failed request, all of which the UI shows as "no season".
   */
  async getWorld() {
    if (!supabase) return { ok: false, season: null, districts: [] };
    try {
      const { data: season, error: seasonError } = await supabase
        .from('reprise_seasons')
        .select('id, season_index, layout_seed, status, started_at, ends_at')
        .in('status', ['active', 'finale']) // finale is still a current season (R5)
        .maybeSingle();
      if (seasonError || !season) return { ok: false, season: null, districts: [] };

      const { data: districts, error: districtError } = await supabase
        .from('reprise_districts')
        .select('district_id, quarter, label, cell_x, cell_y, state, resonance, resonance_required, restored_at')
        .eq('season_id', season.id);
      if (districtError) return { ok: false, season: null, districts: [] };

      return { ok: true, season, districts: districts ?? [] };
    } catch {
      return { ok: false, season: null, districts: [] };
    }
  },

  /**
   * Pour a run's Resonance into a specific district. `districtId` must be an
   * `open` district; the RPC rejects sealed/restored ones. Requires a
   * signed-in caller (the RPC reads auth.uid()); a guest just gets `ok:false`
   * and the run is unaffected. `runId` is the same per-session id Game.js
   * mints for the leaderboard — the RPC keys its replay guard on it, so
   * re-submitting one run never double-counts.
   */
  async contribute({ districtId, amount, runId }) {
    if (!supabase || !districtId || !runId || !(amount > 0)) return { ok: false };
    try {
      const { data, error } = await supabase.rpc('contribute_resonance', {
        p_district_id: districtId,
        p_amount: amount,
        p_run_id: runId,
      });
      if (error) return { ok: false };
      return { ok: true, ...data };
    } catch {
      return { ok: false };
    }
  },

  /**
   * Convenience for the run-end path: find the season's current front (in
   * R0 there is exactly one `open` district) and contribute to it, so the
   * caller never has to know the map's shape. Returns the contribute result
   * plus the district it landed on, or `ok:false` if there's nothing open.
   */
  async contributeToFront({ amount, runId }) {
    const world = await this.getWorld();
    if (!world.ok) return { ok: false };
    const front = world.districts.find((d) => d.state === 'open');
    if (!front) return { ok: false };
    const result = await this.contribute({ districtId: front.district_id, amount, runId });
    return { ...result, district: front };
  },

  /**
   * The run-end contribution dispatcher: during a finale (R5) a run's worth
   * goes as damage to the reformed King; otherwise it pours Resonance into the
   * open front (R0). One world fetch decides which. Returns a tagged result so
   * RunEndOverlay can phrase the outcome. ok:false = no season / offline /
   * nothing to contribute to — the run is simply unaffected.
   */
  async contributeRun({ amount, runId }) {
    if (!supabase) return { ok: false };
    const world = await this.getWorld();
    if (!world.ok) return { ok: false };
    if (world.season.status === 'finale') {
      const res = await this.contributeFinaleDamage({ amount, runId });
      return { ...res, mode: 'finale' };
    }
    const front = world.districts.find((d) => d.state === 'open');
    if (!front) return { ok: false };
    const res = await this.contribute({ districtId: front.district_id, amount, runId });
    return { ...res, mode: 'resonance', district: front };
  },

  /** The finale's shared boss state (R5), or null when no finale is running. */
  async getFinale() {
    if (!supabase) return { ok: false, finale: null };
    try {
      const world = await this.getWorld();
      if (!world.ok) return { ok: false, finale: null };
      const { data, error } = await supabase
        .from('reprise_finale')
        .select('season_id, boss_max_hp, boss_hp, contributor_count, defeated_at')
        .eq('season_id', world.season.id)
        .maybeSingle();
      if (error) return { ok: false, finale: null };
      return { ok: true, finale: data ?? null, season: world.season };
    } catch {
      return { ok: false, finale: null };
    }
  },

  async contributeFinaleDamage({ amount, runId }) {
    if (!supabase || !runId || !(amount > 0)) return { ok: false };
    try {
      const { data, error } = await supabase.rpc('contribute_finale_damage', {
        p_amount: amount,
        p_run_id: runId,
      });
      if (error) return { ok: false };
      return { ok: true, ...data };
    } catch {
      return { ok: false };
    }
  },

  // --- Choirs (R4) ---------------------------------------------------------

  /** This player's choir for the current season, or null. */
  async getMyChoir() {
    if (!supabase) return { ok: false, choir: null };
    try {
      const world = await this.getWorld();
      if (!world.ok) return { ok: false, choir: null };
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;
      if (!userId) return { ok: true, choir: null };
      const { data, error } = await supabase
        .from('reprise_choir_members')
        .select('choir_id, reprise_choirs(name)')
        .eq('season_id', world.season.id)
        .eq('user_id', userId)
        .maybeSingle();
      if (error || !data) return { ok: true, choir: null };
      return { ok: true, choir: { id: data.choir_id, name: data.reprise_choirs?.name ?? 'Choir' } };
    } catch {
      return { ok: false, choir: null };
    }
  },

  /** Choir standings for the current season (ranked by member Resonance). */
  async getChoirStandings() {
    if (!supabase) return { ok: false, standings: [] };
    try {
      const { data, error } = await supabase.rpc('reprise_choir_standings');
      if (error) return { ok: false, standings: [] };
      return { ok: true, standings: data ?? [] };
    } catch {
      return { ok: false, standings: [] };
    }
  },

  async createChoir(name) {
    if (!supabase || !name) return { ok: false };
    try {
      const { data, error } = await supabase.rpc('create_choir', { p_name: name });
      if (error) return { ok: false, error: error.message };
      return { ok: true, ...data };
    } catch { return { ok: false }; }
  },

  async joinChoir(choirId) {
    if (!supabase || !choirId) return { ok: false };
    try {
      const { data, error } = await supabase.rpc('join_choir', { p_choir_id: choirId });
      if (error) return { ok: false, error: error.message };
      return { ok: true, ...data };
    } catch { return { ok: false }; }
  },

  async leaveChoir() {
    if (!supabase) return { ok: false };
    try {
      const { error } = await supabase.rpc('leave_choir');
      if (error) return { ok: false, error: error.message };
      return { ok: true };
    } catch { return { ok: false }; }
  },

  /**
   * The world-buff snapshot Game.js needs at run start (REPRISE.md §5, R2):
   * which districts are restored (server-wide base buffs) and which of those
   * THIS player contributed to (amplified tier). Everything degrades to empty
   * — a guest, no season, or an unreachable server all yield no buffs, so a
   * run is simply unchanged. Shape: `{ restoredIds, contributedIds,
   * restoredCount }`.
   */
  async getBuffState() {
    const empty = { restoredIds: [], contributedIds: [], restoredCount: 0 };
    const world = await this.getWorld();
    if (!world.ok) return empty;
    const restoredIds = world.districts.filter((d) => d.state === 'restored').map((d) => d.district_id);
    if (restoredIds.length === 0 || !supabase) return { restoredIds, contributedIds: [], restoredCount: 0 };

    let contributedIds = [];
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;
      if (userId) {
        // Contributions are publicly readable; scope to this user. A player
        // contributed to a district if they gave Resonance to it OR delivered
        // any fragments (fragments is '{}' when they only gave Resonance, and
        // vice-versa — either non-empty marks a contributor).
        const { data } = await supabase
          .from('reprise_contributions')
          .select('district_id, resonance, fragments')
          .eq('user_id', userId)
          .eq('season_id', world.season.id);
        contributedIds = (data ?? [])
          .filter((r) => r.resonance > 0 || (r.fragments && Object.keys(r.fragments).length > 0))
          .map((r) => r.district_id);
      }
    } catch {
      contributedIds = []; // buffs still apply at base tier
    }
    return { restoredIds, contributedIds, restoredCount: restoredIds.length };
  },

  /**
   * The signed-in player's salvage inventory (REPRISE.md §4). Private per
   * RLS, so this returns `{}` for a guest rather than erroring. Shape is
   * `{ ok, fragments: { fragment_id: { bound, tradeable, total } } }`.
   */
  async getFragments() {
    if (!supabase) return { ok: false, fragments: {} };
    try {
      const { data, error } = await supabase
        .from('reprise_fragments')
        .select('fragment_id, bound_count, tradeable_count');
      if (error) return { ok: false, fragments: {} };
      const fragments = {};
      for (const row of data ?? []) {
        fragments[row.fragment_id] = {
          bound: row.bound_count,
          tradeable: row.tradeable_count,
          total: row.bound_count + row.tradeable_count,
        };
      }
      return { ok: true, fragments };
    } catch {
      return { ok: false, fragments: {} };
    }
  },

  /**
   * Roll `count` fragments (server-side — the client never decides what it
   * gets; see pull_fragments). Spends Shards, debited server-side. Returns
   * `{ ok, rolled: [id...], shards_remaining, cost }`, or `{ ok:false, error }`
   * when the RPC rejects (not signed in, no season, not enough Shards).
   */
  async pull({ count = 1 } = {}) {
    if (!supabase) return { ok: false, error: 'offline' };
    try {
      const { data, error } = await supabase.rpc('pull_fragments', { p_count: count });
      if (error) return { ok: false, error: error.message };
      return { ok: true, ...data };
    } catch {
      return { ok: false, error: 'offline' };
    }
  },

  /**
   * Standing barter offers (REPRISE.md §6). `scope: 'open'` (default) lists
   * fillable offers from others; `scope: 'mine'` lists this player's own open
   * offers to manage. Publicly readable, so browsing works for anyone,
   * though only signed-in players can act. Returns `{ ok, offers }` where
   * each offer is `{ id, from_user, offer, request, created_at }`.
   */
  async getOffers({ scope = 'open' } = {}) {
    if (!supabase) return { ok: false, offers: [] };
    try {
      const world = await this.getWorld();
      if (!world.ok) return { ok: false, offers: [] };
      let query = supabase
        .from('reprise_trades')
        .select('id, from_user, to_user, offer, request, created_at, expires_at')
        .eq('season_id', world.season.id)
        .eq('status', 'open')
        .order('created_at', { ascending: false })
        .limit(60);
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id ?? null;
      if (scope === 'mine') {
        if (!userId) return { ok: true, offers: [] };
        query = query.eq('from_user', userId);
      } else if (userId) {
        query = query.neq('from_user', userId); // don't show me my own on the fill board
      }
      const { data, error } = await query;
      if (error) return { ok: false, offers: [] };
      // Drop expired offers client-side (the RPC rejects them anyway).
      const now = Date.now();
      const offers = (data ?? []).filter((o) => !o.expires_at || new Date(o.expires_at).getTime() > now);

      // Resolve creator usernames (profiles are public) so the board can name
      // who's offering rather than showing raw uuids.
      const ids = [...new Set(offers.map((o) => o.from_user))];
      if (ids.length > 0) {
        const { data: profiles } = await supabase.from('profiles').select('id, username').in('id', ids);
        const names = new Map((profiles ?? []).map((p) => [p.id, p.username]));
        for (const o of offers) o.from_username = names.get(o.from_user) ?? 'a Survivor';
      }
      return { ok: true, offers };
    } catch {
      return { ok: false, offers: [] };
    }
  },

  async createOffer({ offer, request }) {
    if (!supabase || !offer || !request) return { ok: false };
    try {
      const { data, error } = await supabase.rpc('create_trade_offer', { p_offer: offer, p_request: request });
      if (error) return { ok: false, error: error.message };
      return { ok: true, ...data };
    } catch {
      return { ok: false };
    }
  },

  async acceptOffer(tradeId) {
    if (!supabase || !tradeId) return { ok: false };
    try {
      const { data, error } = await supabase.rpc('accept_trade_offer', { p_trade_id: tradeId });
      if (error) return { ok: false, error: error.message };
      return { ok: true, ...data };
    } catch {
      return { ok: false };
    }
  },

  async cancelOffer(tradeId) {
    if (!supabase || !tradeId) return { ok: false };
    try {
      const { data, error } = await supabase.rpc('cancel_trade_offer', { p_trade_id: tradeId });
      if (error) return { ok: false, error: error.message };
      return { ok: true, ...data };
    } catch {
      return { ok: false };
    }
  },

  /**
   * Deliver owned fragments into an open district. `deliveries` is
   * `{ fragment_id: count }`. The server clamps each to what the district
   * still needs and to what the player owns, so an optimistic "deliver
   * everything" is safe. Returns `{ ok, applied, fragments_delivered,
   * restored }`.
   */
  async deliver({ districtId, deliveries }) {
    if (!supabase || !districtId || !deliveries) return { ok: false };
    try {
      const { data, error } = await supabase.rpc('deliver_fragments', {
        p_district_id: districtId,
        p_deliveries: deliveries,
      });
      if (error) return { ok: false, error: error.message };
      return { ok: true, ...data };
    } catch {
      return { ok: false };
    }
  },
};
